/**
 * Phase 12b2 — /app/integrations/ad-platforms.
 *
 * 4-card grid (Meta / Google / LinkedIn / TikTok) with per-card
 * Connect / Test / Disconnect actions. RBAC-gated to
 * `integrations.write`. Real-API OAuth redirect is deferred to backlog
 * `ad-platforms-real-oauth-wiring` — mock mode is the default path.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, auth } from "../../lib/api";
import { useLocale } from "../../lib/i18n";
import { showAgentError } from "../../components/app/AgentErrorToast";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Facebook, Search as GoogleIcon, Linkedin, Music2,
  CheckCircle2, XCircle, AlertCircle, Plug,
} from "lucide-react";

const ICONS = {
  meta: Facebook, google: GoogleIcon, linkedin: Linkedin, tiktok: Music2,
};

const COPY = {
  de: {
    title: "Werbeplattformen", subtitle:
      "Verbinde deine Ad-Konten für Publish + Performance. Mock-Modus " +
      "arbeitet ohne echte API-Zugänge und liefert reproduzierbare Zahlen.",
    real_apis: "Echte APIs aktiv", mock_mode: "Mock-Modus",
    connected: "verbunden", disconnected: "nicht verbunden",
    connect: "Verbinden", test: "Test", disconnect: "Trennen",
    connect_title: (p) => `${p} verbinden`,
    connect_hint_mock: "Mock-Modus — gib eine Konto-ID ein, keine OAuth nötig.",
    connect_hint_real: (envs) =>
      `Echte OAuth benötigt: ${envs.join(", ")}. Backlog: ad-platforms-real-oauth-wiring.`,
    account_id: "Konto-ID (Ad Account)",
    save: "Speichern", cancel: "Abbrechen",
    disconnect_confirm: "Diese Verbindung wirklich trennen?",
    test_ok: "Verbindung OK", test_fail: "Verbindungsfehler",
    test_mocked: "Mock-Antwort", connected_at: "Verbunden am",
  },
  en: {
    title: "Ad Platforms", subtitle:
      "Connect your ad accounts for publish + performance. Mock mode " +
      "runs without real API credentials and returns deterministic numbers.",
    real_apis: "Real APIs live", mock_mode: "Mock mode",
    connected: "connected", disconnected: "disconnected",
    connect: "Connect", test: "Test", disconnect: "Disconnect",
    connect_title: (p) => `Connect ${p}`,
    connect_hint_mock: "Mock mode — enter any ad account ID, no OAuth required.",
    connect_hint_real: (envs) =>
      `Real OAuth needs: ${envs.join(", ")}. Backlog: ad-platforms-real-oauth-wiring.`,
    account_id: "Ad account ID",
    save: "Save", cancel: "Cancel",
    disconnect_confirm: "Really disconnect this integration?",
    test_ok: "Connection OK", test_fail: "Connection failed",
    test_mocked: "Mock response", connected_at: "Connected on",
  },
};

function StatusBadge({ status, t }) {
  if (status === "connected") {
    return (
      <Badge
        data-testid={`adp-status-${status}`}
        variant="outline"
        className="border-emerald-300 bg-emerald-50 text-emerald-800"
      >
        <CheckCircle2 className="size-3 mr-1" />{t.connected}
      </Badge>
    );
  }
  if (status === "invalid") {
    return (
      <Badge
        data-testid="adp-status-invalid"
        variant="outline"
        className="border-rose-300 bg-rose-50 text-rose-800"
      >
        <XCircle className="size-3 mr-1" />invalid
      </Badge>
    );
  }
  return (
    <Badge
      data-testid="adp-status-disconnected"
      variant="outline"
      className="text-muted-foreground"
    >
      {t.disconnected}
    </Badge>
  );
}

function PlatformCard({ platform, realApisEnabled, locale, onChange }) {
  const t = COPY[locale] || COPY.en;
  const Icon = ICONS[platform.platform] || Plug;
  const first = (platform.connections || [])[0];
  const status = first?.status || "disconnected";
  const [modalOpen, setModalOpen] = useState(false);
  const [acctId, setAcctId] = useState("");
  const [busy, setBusy] = useState(false);
  const canWrite = auth.hasPermission("integrations.write");

  async function save() {
    setBusy(true);
    try {
      await api.post(`/ad-platforms/${platform.platform}/connect`,
                        { ad_account_id: acctId });
      toast.success(`${platform.display_name}: ${t.connected}`);
      setModalOpen(false); setAcctId("");
      onChange && onChange();
    } catch (e) {
      showAgentError({ err: e, locale });
    } finally { setBusy(false); }
  }

  async function test() {
    setBusy(true);
    try {
      const r = await api.post(`/ad-platforms/${platform.platform}/test`);
      const label = r.data?.mocked
        ? `${t.test_ok} — ${t.test_mocked}`
        : t.test_ok;
      toast.success(`${platform.display_name}: ${label}`);
    } catch (e) {
      showAgentError({ err: e, locale });
    } finally { setBusy(false); }
  }

  async function disconnect() {
    if (!first?.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(t.disconnect_confirm)) return;
    setBusy(true);
    try {
      await api.delete(`/ad-platforms/connections/${first.id}`);
      toast.success(`${platform.display_name}: ${t.disconnected}`);
      onChange && onChange();
    } catch (e) {
      showAgentError({ err: e, locale });
    } finally { setBusy(false); }
  }

  const envs = platform.capabilities?.real_api_env_vars || [];

  return (
    <Card data-testid={`adp-card-${platform.platform}`}>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-md bg-foreground text-background grid place-items-center">
            <Icon className="size-5" />
          </div>
          <div>
            <CardTitle className="text-base">
              {platform.display_name}
            </CardTitle>
            <div className="text-[10px] font-mono uppercase text-muted-foreground">
              {platform.platform}
            </div>
          </div>
        </div>
        <StatusBadge status={status} t={t} />
      </CardHeader>
      <CardContent className="space-y-3">
        {first ? (
          <div className="text-xs space-y-0.5">
            <div>
              <span className="text-muted-foreground">{t.account_id}:</span>{" "}
              <span
                className="font-mono"
                data-testid={`adp-card-${platform.platform}-account`}
              >
                {first.ad_account_id}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">{t.connected_at}:</span>{" "}
              <span className="tabular-nums">
                {new Date(first.connected_at).toLocaleString(
                    locale === "de" ? "de-DE" : "en-US",
                    { dateStyle: "short", timeStyle: "short" })}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            {realApisEnabled
              ? t.connect_hint_real(envs)
              : t.connect_hint_mock}
          </div>
        )}
        <div className="flex gap-2">
          {first ? (
            <>
              <Button
                variant="outline" size="sm"
                onClick={test}
                disabled={busy || !canWrite}
                data-testid={`adp-card-${platform.platform}-test`}
              >{t.test}</Button>
              <Button
                variant="outline" size="sm"
                onClick={disconnect}
                disabled={busy || !canWrite}
                data-testid={`adp-card-${platform.platform}-disconnect`}
              >{t.disconnect}</Button>
            </>
          ) : (
            <Button
              size="sm"
              onClick={() => setModalOpen(true)}
              disabled={busy || !canWrite || realApisEnabled}
              data-testid={`adp-card-${platform.platform}-connect`}
            >{t.connect}</Button>
          )}
        </div>
      </CardContent>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          data-testid={`adp-modal-${platform.platform}`}
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle>
              {t.connect_title(platform.display_name)}
            </DialogTitle>
            <DialogDescription>
              {realApisEnabled
                ? t.connect_hint_real(envs)
                : t.connect_hint_mock}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`adp-acct-${platform.platform}`}>
              {t.account_id}
            </Label>
            <Input
              id={`adp-acct-${platform.platform}`}
              data-testid={`adp-modal-${platform.platform}-acct-input`}
              value={acctId}
              onChange={(e) => setAcctId(e.target.value)}
              placeholder="fb_act_1234567890"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={busy}
              data-testid={`adp-modal-${platform.platform}-cancel`}
            >{t.cancel}</Button>
            <Button
              onClick={save}
              disabled={busy || !acctId.trim() || realApisEnabled}
              data-testid={`adp-modal-${platform.platform}-save`}
            >{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function AdPlatforms() {
  const { locale } = useLocale();
  const t = COPY[locale] || COPY.en;
  const [data, setData] = useState({ platforms: [], real_apis_enabled: false });
  const [loading, setLoading] = useState(true);
  const [refetch, setRefetch] = useState(0);
  // Batch-1: tenant-scoped Meta live flag. Separate from the global
  // operator gate. The card shows both plus the effective boolean.
  const [metaFlag, setMetaFlag] = useState(null);
  const [metaFlagBusy, setMetaFlagBusy] = useState(false);
  const canWrite = auth.hasPermission("integrations.write");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/ad-platforms")
      .then((r) => { if (alive) setData(r.data || { platforms: [] }); })
      .catch(() => { if (alive) setData({ platforms: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    // Batch-1: tenant-scoped Meta flag — separate fetch so it doesn't
    // block the platforms grid. Failures fall through to `null` and
    // the flag banner simply isn't rendered.
    api.get("/ad-platforms/meta/tenant-flag", { skipPermissionToast: true })
      .then((r) => { if (alive) setMetaFlag(r.data); })
      .catch(() => { if (alive) setMetaFlag(null); });
    return () => { alive = false; };
  }, [refetch]);

  async function toggleMetaFlag() {
    if (!metaFlag) return;
    setMetaFlagBusy(true);
    try {
      const r = await api.put("/ad-platforms/meta/tenant-flag",
        { enabled: !metaFlag.tenant_enabled });
      setMetaFlag(r.data);
      toast.success(
        r.data.tenant_enabled
          ? (locale === "de" ? "Meta-Live für diesen Tenant aktiviert."
                                    : "Meta live enabled for this tenant.")
          : (locale === "de" ? "Meta-Live für diesen Tenant deaktiviert."
                                    : "Meta live disabled for this tenant."));
    } catch (e) {
      showAgentError({ err: e, locale });
    } finally {
      setMetaFlagBusy(false);
    }
  }

  return (
    <div className="p-6 space-y-5" data-testid="ad-platforms-page">
      <header className="flex items-start gap-3">
        <div className="size-10 rounded-md bg-foreground text-background grid place-items-center">
          <Plug className="size-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">{t.title}</h1>
            <Badge
              data-testid="adp-mode-badge"
              variant="outline"
              className={data.real_apis_enabled
                ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                : "text-muted-foreground"}
            >
              <AlertCircle className="size-3 mr-1" />
              {data.real_apis_enabled ? t.real_apis : t.mock_mode}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{t.subtitle}</p>
        </div>
      </header>

      {/* Batch-1: Meta tenant-flag banner. Only rendered when the
          endpoint reported a payload (permission granted). Uses the
          same 5-stage visual grammar as Phase 4 connection status:
          disabled → allowed-by-tenant → gated-by-operator → armed → live. */}
      {metaFlag && (
        <Card data-testid="adp-meta-tenant-flag-card"
                className="border-dashed">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <Facebook className="size-4" />
                <div className="text-sm font-medium">
                  {locale === "de"
                    ? "Meta Live-Publishing für diesen Tenant"
                    : "Meta live publishing for this tenant"}
                </div>
                <Badge
                  variant="outline"
                  data-testid="adp-meta-tenant-flag-status"
                  className={metaFlag.effective_live_enabled
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                    : metaFlag.tenant_enabled
                      ? "border-amber-300 bg-amber-50 text-amber-800"
                      : "text-muted-foreground"}
                >
                  {metaFlag.effective_live_enabled
                    ? (locale === "de" ? "Live aktiv" : "Live active")
                    : metaFlag.tenant_enabled
                      ? (locale === "de"
                          ? "Freigegeben — Betreiber-Gate fehlt"
                          : "Allowed — operator gate pending")
                      : (locale === "de" ? "Deaktiviert" : "Disabled")}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {locale === "de"
                  ? "Setzt nur den Tenant-Flag. Der globale Betreiber-Gate "
                    + "bleibt eine Operator-Route und wird separat aktiviert."
                  : "Toggles the tenant flag only. The global operator gate "
                    + "remains an operator-route concern and is enabled "
                    + "separately."}
              </p>
              <div className="text-[10px] text-muted-foreground font-mono">
                {locale === "de" ? "Tenant-Flag" : "Tenant flag"}:{" "}
                {metaFlag.tenant_enabled ? "on" : "off"}
                {" · "}
                {locale === "de" ? "Betreiber-Gate" : "Operator gate"}:{" "}
                {metaFlag.operator_gate_enabled ? "on" : "off"}
              </div>
            </div>
            <Button
              size="sm"
              variant={metaFlag.tenant_enabled ? "outline" : "default"}
              disabled={metaFlagBusy || !canWrite}
              onClick={toggleMetaFlag}
              data-testid="adp-meta-tenant-flag-toggle"
            >
              {metaFlagBusy
                ? (locale === "de" ? "Speichern…" : "Saving…")
                : metaFlag.tenant_enabled
                  ? (locale === "de" ? "Deaktivieren" : "Disable")
                  : (locale === "de" ? "Aktivieren" : "Enable")}
            </Button>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div
          data-testid="ad-platforms-loading"
          className="text-sm text-muted-foreground"
        >
          Loading…
        </div>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="ad-platforms-grid"
        >
          {(data.platforms || []).map((p) => (
            <PlatformCard
              key={p.platform}
              platform={p}
              realApisEnabled={data.real_apis_enabled}
              locale={locale}
              onChange={() => setRefetch((x) => x + 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
